import React from "react";
import ReactPlayer from "react-player";
import { TooltipWrapper } from "ui";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  InvalidLabel,
  MessageBlock,
  MessageBlockProps,
  MessageContainer,
} from "../../message/styled";
import { Attachment } from "types/conversationInbox";
import { MediaTemplate } from "./media-template";
import { MediaLocation } from "./media-location";
import { MediaShare } from "./media-share";
import { MediaFile } from "./media-file";

export enum MediaTypes {
  storyMention = "story_mention",
  image = "image",
  audio = "audio",
  share = "share",
  template = "template",
  video = "video",
  file = "file",
  location = "location",
  gif = "gif",
  sticker = "sticker",
  fallback = "fallback",
}

type UnsupportedMediaProps = {
  message?: string;
  radius?: boolean;
} & MessageBlockProps;

export type MultiMediaProps = {
  media: Attachment;
  onError: () => void;
  showInvalid: boolean;
  isReady: boolean;
  setIsReady: () => void;
  portraitMedia?: boolean;
} & UnsupportedMediaProps;

export const UnsupportedMedia = ({
  message,
  ...props
}: UnsupportedMediaProps): JSX.Element => {
  return (
    <MessageContainer left={props.left}>
      <MessageBlock {...props}>
        <UnsupportedMessageContent message={message} />
      </MessageBlock>
    </MessageContainer>
  );
};

export const UnsupportedMessageContent = ({
  message,
}: {
  message: React.ReactNode;
}) => (
  <UnsupportedMediaWrapper>
    Shared an attachment
    <TooltipWrapper
      tooltip={message || "We’re unable to display this attachment."}
      placement="bottom"
    >
      <span data-testid="shared-an-attachment-tooltip">?</span>
    </TooltipWrapper>
  </UnsupportedMediaWrapper>
);

export const resolveMediaType = (media: Attachment): MediaTypes | string => {
  if (!media || !media.type) {
    return MediaTypes.fallback;
  }

  if (media.type.includes("image")) {
    return MediaTypes.image;
  }

  if (media.type.includes("video")) {
    return MediaTypes.video;
  }

  if (media.type.includes("audio")) {
    return MediaTypes.audio;
  }

  if (media.type === MediaTypes.fallback && media?.payload?.url) {
    return MediaTypes.share;
  }

  return media.type;
};

export function MultiMedia(props: MultiMediaProps): JSX.Element {
  const { media, onError, showInvalid, isReady, setIsReady } = props;

  if (showInvalid) {
    return (
      <MediaUnavailable portraitMedia={!!props.portraitMedia}>
        <i
          data-testid="invalid-icon"
          className="fa-regular fa-clock"
          style={{
            color: "var(--ink-secondary)",
            fontSize: "34px",
            width: "100% !important",
          }}
        ></i>

        <InvalidLabel>No longer available in Sked</InvalidLabel>
      </MediaUnavailable>
    );
  }

  const mediaType = resolveMediaType(media);
  let mediaContent;

  switch (mediaType) {
    case MediaTypes.image: {
      mediaContent = (
        <img
          data-testid="multimedia-image"
          src={media.payload.url}
          width="100%"
          onError={onError}
          onLoad={setIsReady}
        />
      );
      break;
    }

    case MediaTypes.video: {
      mediaContent = (
        <ReactPlayer
          src={media.payload.url}
          width="100%"
          height="auto"
          controls
          onError={onError}
          onReady={setIsReady}
        />
      );
      break;
    }

    case MediaTypes.audio: {
      mediaContent = (
        <ReactPlayer
          src={media.payload.url}
          width="100%"
          height="40px"
          controls
          onError={onError}
          onReady={setIsReady}
        />
      );
      break;
    }

    case MediaTypes.share: {
      mediaContent = <MediaShare {...props} />;
      break;
    }

    case MediaTypes.template: {
      mediaContent = <MediaTemplate {...props} />;
      break;
    }

    case MediaTypes.location: {
      mediaContent = <MediaLocation {...props} />;
      break;
    }

    case MediaTypes.file: {
      mediaContent = <MediaFile {...props} />;
      break;
    }

    default: {
      mediaContent = null;
      break;
    }
  }

  if (!mediaContent) {
    return (
      <div style={{ display: "block" }}>
        <UnsupportedMedia {...props} removeMargin />
      </div>
    );
  }

  return (
    <>
      {!isReady && (
        <div
          data-testid="loading-spinner"
          className="absolute inset-0 flex items-center justify-center"
        >
          <Spinner size="sm" />
        </div>
      )}
      <div className={cn("hidden", isReady && "block")}>{mediaContent}</div>
    </>
  );
}

function MediaUnavailable({
  portraitMedia,
  children,
}: {
  portraitMedia: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ margin: portraitMedia ? "115px auto" : "45px auto" }}>
      {children}
    </div>
  );
}

export function UnsupportedMediaWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="flex [&_span]:inline-flex [&_span]:ml-[10px] [&_span]:w-[19px] [&_span]:h-[19px] [&_span]:bg-ink-secondary [&_span]:text-xs [&_span]:text-ink-inverse [&_span]:rounded-full [&_span]:justify-center [&_span]:items-center [&_span]:font-semibold [&_span]:cursor-pointer [&_a]:inline-block [&_a]:ml-[5px]">
      {children}
    </div>
  );
}
